/**
 * Safety Control Workspace — Scans register organization.
 * Current/latest relevant scan per case+definition; history is subordinate.
 */

import type { ScanRegisterRow, ScanWorkflowState } from './workspaceRegisters';
import { isEmptyRegisterValue } from './workspaceStatusDimensions';

export const AD_HOC_CASE_GROUP_LABEL = 'Ad hoc / No related Safety Case';

/** Relevance band for picking the primary scan in a case+definition group (lower = higher priority). */
export type ScanRelevanceBand =
  | 'active_execution'
  | 'awaiting_signoff'
  | 'coverage_issues'
  | 'signed_or_terminal';

const BAND_RANK: Record<ScanRelevanceBand, number> = {
  active_execution: 0,
  awaiting_signoff: 1,
  coverage_issues: 2,
  signed_or_terminal: 3,
};

export type ScanOrganizeGroup = {
  caseKey: string;
  caseLabel: string;
  definition: string;
  isAdHoc: boolean;
  current: ScanRegisterRow;
  history: ScanRegisterRow[];
};

export type ScanColumnVisibility = {
  showDefinition: boolean;
  showWorkflow: boolean;
  showCoverage: boolean;
  showResult: boolean;
  showTrigger: boolean;
};

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function dateTimestamp(value?: string | null): number {
  const ms = new Date(String(value || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** True when the scan has no case id/key — do not invent a Safety Case association. */
export function isAdHocScanRow(row: ScanRegisterRow): boolean {
  const caseId = String(row.caseId || '').trim();
  const caseKey = String(row.caseKey || '').trim();
  return !caseId && !caseKey;
}

export function scanCaseGroupKey(row: ScanRegisterRow): string {
  if (isAdHocScanRow(row)) return '__ad_hoc__';
  return String(row.caseKey || row.caseId || '').trim() || '__ad_hoc__';
}

export function scanCaseGroupLabel(row: ScanRegisterRow): string {
  if (isAdHocScanRow(row)) return AD_HOC_CASE_GROUP_LABEL;
  const related = String(row.relatedCase || '').trim();
  if (related && !isEmptyRegisterValue(related)) return related;
  return String(row.caseKey || row.caseId || '').trim() || AD_HOC_CASE_GROUP_LABEL;
}

export function scanDefinitionGroupKey(row: ScanRegisterRow): string {
  const definition = String(row.definition || '').trim();
  if (!definition || isEmptyRegisterValue(definition)) return '__none__';
  return normalize(definition);
}

export function scanLogicalGroupKey(row: ScanRegisterRow): string {
  return String(row.logicalScanKey || row.reportId || row.id).trim() || row.id;
}

/**
 * Priority bands (frozen):
 * 1. Running, scheduled, failed, or blocked (open execution)
 * 2. Awaiting sign-off
 * 3. Coverage issues
 * 4. Latest signed / terminal only when no newer open/active scan exists
 */
export function scanRelevanceBand(row: ScanRegisterRow): ScanRelevanceBand {
  const state = row.workflowState as ScanWorkflowState;
  if (
    state === 'Running'
    || state === 'Scheduled'
    || state === 'Queued'
    || state === 'Failed'
    || state === 'Review required'
  ) {
    return 'active_execution';
  }
  if (state === 'Awaiting sign-off') return 'awaiting_signoff';
  if (
    state === 'Insufficient coverage'
    || normalize(row.coverage) === 'insufficient coverage'
  ) {
    return 'coverage_issues';
  }
  return 'signed_or_terminal';
}

function compareRelevanceThenRecency(left: ScanRegisterRow, right: ScanRegisterRow): number {
  const bandCmp = BAND_RANK[scanRelevanceBand(left)] - BAND_RANK[scanRelevanceBand(right)];
  if (bandCmp !== 0) return bandCmp;
  const timeCmp = dateTimestamp(right.completedAt) - dateTimestamp(left.completedAt);
  if (timeCmp !== 0) return timeCmp;
  return String(right.id).localeCompare(String(left.id));
}

/** Pick the current/latest relevant scan among peers for one case+definition pair. */
export function selectCurrentRelevantScan(rows: ScanRegisterRow[]): ScanRegisterRow | null {
  if (!rows.length) return null;
  return [...rows].sort(compareRelevanceThenRecency)[0] || null;
}

/**
 * Group by Safety Case → Definition → logical Scan lineage.
 * Revisions/retries of one logical Scan collapse into one row; separate windows stay separate.
 * Ad hoc / no-case scans share a single honest group label.
 */
export function groupScansForRegister(rows: ScanRegisterRow[]): ScanOrganizeGroup[] {
  const buckets = new Map<string, ScanRegisterRow[]>();
  for (const row of rows) {
    const key = `${scanCaseGroupKey(row)}::${scanDefinitionGroupKey(row)}::${scanLogicalGroupKey(row)}`;
    const list = buckets.get(key);
    if (list) list.push(row);
    else buckets.set(key, [row]);
  }

  const groups: ScanOrganizeGroup[] = [];
  for (const peers of Array.from(buckets.values())) {
    const ordered = [...peers].sort(compareRelevanceThenRecency);
    const current = ordered[0];
    if (!current) continue;
    const history = ordered.slice(1).sort((a, b) => {
      const timeCmp = dateTimestamp(b.completedAt) - dateTimestamp(a.completedAt);
      if (timeCmp !== 0) return timeCmp;
      return String(b.id).localeCompare(String(a.id));
    });
    groups.push({
      caseKey: scanCaseGroupKey(current),
      caseLabel: scanCaseGroupLabel(current),
      definition: String(current.definition || '').trim() || '—',
      isAdHoc: isAdHocScanRow(current),
      current,
      history,
    });
  }

  groups.sort((left, right) => {
    if (left.isAdHoc !== right.isAdHoc) return left.isAdHoc ? 1 : -1;
    const caseCmp = left.caseLabel.localeCompare(right.caseLabel, undefined, { sensitivity: 'base' });
    if (caseCmp !== 0) return caseCmp;
    const defCmp = left.definition.localeCompare(right.definition, undefined, { sensitivity: 'base' });
    if (defCmp !== 0) return defCmp;
    return compareRelevanceThenRecency(left.current, right.current);
  });

  return groups;
}

/** Keep only the current/relevant scan per case+definition (Latest per case view). */
export function filterToLatestRelevantScans(rows: ScanRegisterRow[]): ScanRegisterRow[] {
  return groupScansForRegister(rows).map((group) => group.current);
}

/** Scan cell subtitle: zone · window · trigger (omit empties). */
export function buildScanCellSubtitle(row: ScanRegisterRow): string {
  const parts = [row.siteZone, row.windowLabel, row.trigger]
    .map((part) => String(part || '').trim())
    .filter((part) => part && !isEmptyRegisterValue(part));
  return parts.join(' · ');
}

/** Primary Scan cell title: prefer definition when distinct from scan label. */
export function buildScanCellTitle(row: ScanRegisterRow): string {
  const label = String(row.scanLabel || '').trim();
  const definition = String(row.definition || '').trim();
  if (definition && !isEmptyRegisterValue(definition) && normalize(definition) !== normalize(label)) {
    return label || definition;
  }
  return label || definition || '—';
}

function uniqueNonEmpty(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = String(value || '').trim();
    if (!trimmed || isEmptyRegisterValue(trimmed)) continue;
    const key = normalize(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/** Hide empty columns and constant Definition/Workflow/Trigger across the visible set. */
export function resolveScanColumnVisibility(rows: ScanRegisterRow[]): ScanColumnVisibility {
  const definitions = uniqueNonEmpty(rows.map((row) => row.definition));
  const workflows = uniqueNonEmpty(rows.map((row) => row.workflowState));
  const triggers = uniqueNonEmpty(rows.map((row) => row.trigger));
  return {
    showDefinition: definitions.length > 1,
    // Hide when constant across the visible set (saved view already names the job).
    showWorkflow: workflows.length > 1,
    showCoverage: rows.some((row) => !isEmptyRegisterValue(row.coverage)),
    showResult: rows.some((row) => !isEmptyRegisterValue(row.result)),
    showTrigger: triggers.length > 1,
  };
}

export function scanDefinitionScopeHeading(definitionName: string | null | undefined): string | null {
  const name = String(definitionName || '').trim();
  if (!name) return null;
  return 'Showing current and latest Safety Scans for this definition';
}
